/**
 * OptionsWorkspace — the Charts page's 4th layout: an option-trading workspace for
 * Delta BTC / ETH options.
 *
 *   ┌ top bar: BTC|ETH · index · ATM IV · expiry pills · Chart · Classic|Workspace ┐
 *   │ chart strip (the page's own spot chart + leg strikes, breakevens, profit zone) │
 *   ├──────────── Workspace ─────────────┬─────────────┬──────────────────────────┤
 *   │ option chain                       │ positions    │ analytics (payoff, P&L   │
 *   │ (click bid = sell, ask = buy, S/B)  │ + ready-made │ table, heatmap, greeks)  │
 *   └────────────────────────────────────┴─────────────┴──────────────────────────┘
 *   Classic: a horizontal strike strip on top, positions | analytics below.
 *
 * Legs live in state as the user's DECISIONS; live prices are layered on each render
 * (MKT legs follow the mark, every leg's IV follows its contract), so a 3s chain poll
 * never fights an edit in progress.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useWsChain, atmOf, legFromRow, markOf, execPrice, WsRow, WsChain, useDailyBars, entryContext } from './wsData';
import { WsLeg, OptType, Side, computeStats, positionPnl, horizonMs, isNewLeg, isMonthlyExpiry } from './wsMath';
import { useWsColors, readPrefs, writePrefs, WsPrefs, fmtStrike, fmtPx, fmtMoney, expLabel, timeLeft } from './wsUi';
import ChainTable from './ChainTable';
import ChainStrip from './ChainStrip';
import LegsPanel from './LegsPanel';
import AnalyticsPanel, { WhatIf } from './AnalyticsPanel';
import BasketChart from './BasketChart';

export interface WsChartOverlay {
  levels: Array<{ type: string; id?: string; price: number; color: string; lineStyle?: number; draggable?: boolean; axisLabel?: string; noAxisLabel?: boolean }>;
  zones: Array<{ top: number; bottom: number; fill: string; edge?: string; label?: string }>;
}

interface Props {
  base: 'BTC' | 'ETH';
  onBaseChange: (b: 'BTC' | 'ETH') => void;
  /** Renders the page's chart (spot, or the charted contract) with the workspace overlay merged in. */
  renderChart: (overlay: WsChartOverlay) => React.ReactNode;
  /** Option contract currently in the chart strip (null = the underlying). */
  chartContract: string | null;
  onChartContract: (symbol: string | null) => void;
  /** An expiry pill was clicked: the chart's FNO lines and panes follow that expiry. */
  onExpiryPick?: (iso: string) => void;
  /** The expiry the chart's FNO data is showing (marks its pill). */
  fnoExpiry?: string | null;
}

const LEGS_KEY = 'charts_optionsWorkspace_legs_v1';

/** Apply live quotes: MKT legs take the executable price (touch model: sell at the bid, buy
 *  at the ask; mark model: the mark), every leg takes its contract's IV and live mark, and a
 *  held leg being rolled is booked at the price closing it would get (a short is bought
 *  back at the ask, a long sold at the bid). */
function priceLegs(legs: WsLeg[], chain: WsChain | null, fallbackIv: number, model: 'touch' | 'mark' = 'touch'): WsLeg[] {
  if (!chain) return legs;
  const rowFor = (l: WsLeg) => (chain.chain?.iso === l.expiry ? chain.chain.rows.find(r => r.strike === l.strike) : undefined);
  return legs.map(l => {
    const row = rowFor(l);
    const q = row ? (l.optionType === 'CE' ? row.ce : row.pe) : chain.quotes[l.symbol];
    const mk = markOf(q);
    const exp = chain.expiries.find(e => e.iso === l.expiry);
    return {
      ...l,
      settleMs: exp?.settleMs ?? l.settleMs,
      iv: q?.iv || l.iv || fallbackIv,
      price: !l.held && l.priceMode === 'MKT' ? (execPrice(q, l.action, model) ?? l.price) : l.price,
      mark: mk ?? l.mark,
      closeMark: l.closing ? (execPrice(q, l.action === 'SELL' ? 'BUY' : 'SELL', model) ?? l.closeMark ?? l.price) : undefined,
    };
  });
}

const OptionsWorkspace: React.FC<Props> = ({ base, onBaseChange, renderChart, chartContract, onChartContract, onExpiryPick, fnoExpiry }) => {
  const c = useWsColors();
  const [prefs, setPrefsState] = useState<WsPrefs>(() => readPrefs());
  const setPrefs = useCallback((p: Partial<WsPrefs>) => setPrefsState(prev => { const n = { ...prev, ...p }; writePrefs(n); return n; }), []);
  const [expiry, setExpiry] = useState<string | null>(null);
  const [legs, setLegsState] = useState<WsLeg[]>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(LEGS_KEY) || 'null');
      return saved?.base === base && Array.isArray(saved.legs) ? saved.legs : [];
    } catch { return []; }
  });
  const setLegs = useCallback((fn: (prev: WsLeg[]) => WsLeg[]) => setLegsState(prev => fn(prev)), []);
  const [whatIf, setWhatIf] = useState<WhatIf>({ spotPct: 0, timeFrac: 0, ivShift: 0 });
  const [recentre, setRecentre] = useState(0);
  const hoverRef = useRef<{ row: WsRow; t: OptType } | null>(null);
  // The strip shows the whole basket's combined premium / P&L as candles.
  const [basketView, setBasketView] = useState(false);

  // Keep the basket across a page refresh within the session (per base).
  useEffect(() => { try { sessionStorage.setItem(LEGS_KEY, JSON.stringify({ base, legs })); } catch { /* storage blocked */ } }, [base, legs]);
  // Switching asset starts a fresh basket on its nearest expiry.
  const baseRef = useRef(base);
  useEffect(() => {
    if (baseRef.current === base) return;
    baseRef.current = base;
    setLegsState([]); setExpiry(null); setWhatIf({ spotPct: 0, timeFrac: 0, ivShift: 0 });
  }, [base]);

  const legSymbols = useMemo(() => legs.map(l => l.symbol), [legs]);
  const { data: chain, error, lastAt } = useWsChain(base, expiry, legSymbols);

  // An expiry that has settled drops out of the list — fall back to the nearest.
  useEffect(() => {
    if (expiry && chain && !chain.expiries.some(e => e.iso === expiry)) setExpiry(null);
  }, [chain, expiry]);

  const spot = chain?.spot || 0;
  const cv = chain?.contractValue || (base === 'BTC' ? 0.001 : 0.01);
  const atm = useMemo(() => atmOf(chain?.chain?.rows || [], spot), [chain, spot]);
  const atmIv = atm.iv || 0;

  const pricedLegs = useMemo(() => priceLegs(legs, chain, atmIv || 0.5, prefs.fillModel), [legs, chain, atmIv, prefs.fillModel]);
  const stats = useMemo(() => computeStats(pricedLegs, spot, cv, atmIv, Date.now()), [pricedLegs, spot, cv, atmIv]);
  // Close / roll PREVIEW: with held legs marked CLOSE (or replacements pending), the analytics show
  // the position AFTER the change, and this is the position as it is now, drawn dashed beside it.
  const compare = useMemo(() => {
    const closing = pricedLegs.filter(l => l.held && l.closing && l.enabled).length;
    const opening = pricedLegs.filter(l => l.rollBasketId != null && l.enabled).length;
    if (!closing && !opening) return null;
    const now = pricedLegs.filter(l => l.rollBasketId == null).map(l => (l.closing ? { ...l, closing: false, closeMark: undefined } : l));
    return { legs: now, stats: computeStats(now, spot, cv, atmIv, Date.now()), closing, opening };
  }, [pricedLegs, spot, cv, atmIv]);

  const onAdd = useCallback((row: WsRow, t: OptType, action: Side) => {
    if (!chain?.chain) return;
    const ch = chain.chain;
    setLegs(prev => {
      const same = prev.find(l => isNewLeg(l) && l.expiry === ch.iso && l.strike === row.strike && l.optionType === t && l.action === action);
      if (same) return prev.map(l => (l === same ? { ...l, lots: Math.min(10000, l.lots + 1) } : l));
      const leg = legFromRow(base, row, t, action, ch, 1, atmIv || 0.5);
      return leg ? [...prev, leg] : prev;
    });
  }, [chain, base, atmIv, setLegs]);

  // Keyboard: hover a side of a strike and press S / B.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const tg = e.target as HTMLElement | null;
      if (tg && (tg.tagName === 'INPUT' || tg.tagName === 'TEXTAREA' || tg.tagName === 'SELECT' || tg.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if ((k !== 's' && k !== 'b') || !hoverRef.current) return;
      e.preventDefault();
      onAdd(hoverRef.current.row, hoverRef.current.t, k === 's' ? 'SELL' : 'BUY');
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onAdd]);
  const onHover = useCallback((h: { row: WsRow; t: OptType } | null) => { hoverRef.current = h; }, []);
  const chartIt = useCallback((sym: string) => { setBasketView(false); onChartContract(sym); setPrefs({ chartOpen: true }); }, [onChartContract, setPrefs]);
  const chartBasket = useCallback(() => { setBasketView(true); setPrefs({ chartOpen: true }); }, [setPrefs]);

  // ── chart-strip overlay: leg strikes, breakevens, profit zone(s) at the horizon ──
  const overlay = useMemo<WsChartOverlay>(() => {
    const levels: WsChartOverlay['levels'] = [];
    const zones: WsChartOverlay['zones'] = [];
    if (chartContract) {
      // Premium chart of one contract: strikes/breakevens don't belong on it - show the
      // entry (fill / planned) price of every leg on this contract instead.
      pricedLegs.filter(l => l.symbol === chartContract && l.price > 0).forEach((l, i) => levels.push({
        type: 'WSLEG', id: `ent${i}`, price: l.price, draggable: false, lineStyle: 2,
        color: l.action === 'BUY' ? c.green : c.red, axisLabel: `${l.held ? 'Fill' : 'Entry'} ${l.action[0]} $${l.price >= 100 ? l.price.toFixed(1) : l.price.toFixed(2)}`,
      }));
      return { levels, zones };
    }
    const on = pricedLegs.filter(l => l.enabled && !l.closing);
    const seen = new Set<string>();
    for (const l of on) {
      const key = `${l.action}${l.optionType}${l.strike}`;
      if (seen.has(key)) continue;
      seen.add(key);
      // Line only (2026-11-03): the axis tags sat over the FNO option-profile histogram on the
      // right; the zones and the line colours carry the meaning (strike: green buy / red sell).
      levels.push({ type: 'WSLEG', id: key, price: l.strike, draggable: false, lineStyle: 2, noAxisLabel: true,
        color: l.action === 'BUY' ? c.green : c.red, axisLabel: `${l.action[0]} ${fmtStrike(l.strike)} ${l.optionType}` });
    }
    stats.breakevens.forEach((b, i) => levels.push({ type: 'WSBE', id: `be${i}`, price: b, draggable: false, lineStyle: 1, noAxisLabel: true, color: c.amber, axisLabel: `BE ${fmtStrike(Math.round(b))}` }));
    const H = horizonMs(on);
    if (H != null && spot > 0) {
      const cuts = [spot * 0.5, ...stats.breakevens, spot * 1.5];
      for (let i = 0; i < cuts.length - 1; i++) {
        const lo = cuts[i], hi = cuts[i + 1];
        if (positionPnl(on, (lo + hi) / 2, H, cv) > 0) {
          zones.push({ top: hi, bottom: lo, fill: c.isDark ? 'rgba(38,194,129,0.07)' : 'rgba(11,122,75,0.07)', edge: 'rgba(38,194,129,0.35)', label: 'Profit at expiry' });
        }
      }
    }
    return { levels, zones };
  }, [pricedLegs, stats.breakevens, spot, cv, c, chartContract]);

  // ── chart strip resize (drag the divider, or a size preset) ──
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ y: number; h: number } | null>(null);
  /** Tallest strip that still leaves the panels a usable 150 px below it. */
  const maxStrip = () => {
    const h = rootRef.current?.clientHeight || window.innerHeight;
    return Math.max(160, h - 44 - 26 - 150);   // top bar ≈44, divider 26
  };
  const setStrip = (frac: number) => {
    const h = rootRef.current?.clientHeight || window.innerHeight;
    setPrefs({ chartOpen: true, lowerCollapsed: false, chartHeight: Math.round(Math.max(140, Math.min(maxStrip(), (h - 44 - 26) * frac))) });
  };
  useEffect(() => {
    const mv = (e: MouseEvent) => {
      if (!dragRef.current) return;
      setPrefsState(p => ({ ...p, chartHeight: Math.max(140, Math.min(maxStrip(), dragRef.current!.h + e.clientY - dragRef.current!.y)) }));
    };
    const up = () => { if (dragRef.current) { dragRef.current = null; setPrefsState(p => { writePrefs(p); return p; }); } };
    window.addEventListener('mousemove', mv); window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
  }, []);

  const bars = useDailyBars(base);
  const oiData = useMemo(() => (chain?.chain
    ? { iso: chain.chain.iso, rows: chain.chain.rows.map(r => ({ strike: r.strike, ceOi: r.ce?.oi || 0, peOi: r.pe?.oi || 0 })) }
    : null), [chain]);
  const ctx3 = useMemo(() => entryContext(bars), [bars]);
  const stale = !lastAt || Date.now() - lastAt > 12000;
  const basketLegs = useMemo(() => pricedLegs.filter(l => l.enabled && !l.closing && l.symbol && l.price > 0), [pricedLegs]);
  const selIso = chain?.chain?.iso || null;

  const seg = (opts: Array<[string, string]>, value: string, set: (v: string) => void, small = false) => (
    <Box sx={{ display: 'flex', bgcolor: c.hover, borderRadius: 1, p: 0.25 }}>
      {opts.map(([k, l]) => (
        <Box key={k} component="button" type="button" onClick={() => set(k)}
          sx={{ all: 'unset', cursor: 'pointer', fontSize: small ? 11 : 12, px: small ? 0.7 : 1, py: 0.2, borderRadius: 0.8, whiteSpace: 'nowrap',
            fontWeight: value === k ? 700 : 500, color: value === k ? c.text : c.dim, bgcolor: value === k ? c.panel : 'transparent', boxShadow: value === k ? 1 : 0 }}>{l}</Box>
      ))}
    </Box>
  );

  const chainControls = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 0.8, py: 0.5, borderBottom: `1px solid ${c.border}`, flexWrap: 'wrap' }}>
      {prefs.view === 'workspace' && seg([['both', 'Calls & Puts'], ['CE', 'Calls'], ['PE', 'Puts']], prefs.chainSide, v => setPrefs({ chainSide: v as any }), true)}
      {prefs.view === 'workspace' && seg([['iv', 'IV'], ['greeks', 'Δ'], ['none', '—']], prefs.chainCols, v => setPrefs({ chainCols: v as any }), true)}
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Strikes shown around ATM">
        <Box component="select" value={prefs.range} onChange={(e: any) => setPrefs({ range: Number(e.target.value) })}
          sx={{ all: 'unset', fontSize: 11, color: c.dim, cursor: 'pointer', border: `1px solid ${c.border}`, borderRadius: 0.8, px: 0.6, py: 0.15, '& option': { color: '#000' } }}>
          {[5, 10, 15, 0].map(n => <option key={n} value={n}>{n ? `±${n}` : 'All'}</option>)}
        </Box>
      </Tooltip>
      <Tooltip title="Centre on ATM"><IconButton size="small" sx={{ p: 0.3 }} onClick={() => setRecentre(n => n + 1)}><MyLocationIcon sx={{ fontSize: 15, color: c.amber }} /></IconButton></Tooltip>
      {prefs.view === 'workspace' && (
        <Tooltip title="Collapse chain"><IconButton size="small" sx={{ p: 0.3 }} onClick={() => setPrefs({ chainCollapsed: true })}><KeyboardDoubleArrowLeftIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      )}
    </Box>
  );

  const legsPanel = (compact = false) => (
    <LegsPanel base={base} chain={chain} legs={pricedLegs} compact={compact}
      setLegs={fn => setLegs(prev => {
        // LegsPanel edits the PRICED view; map its result back onto decisions (price kept
        // only for LMT/held legs — MKT legs re-take the mark on every render anyway).
        const next = fn(priceLegs(prev, chain, atmIv || 0.5, prefs.fillModel));
        return next;
      })}
      stats={stats} spot={spot} atmIv={atmIv} ccy={prefs.ccy} c={c} prefs={prefs} setPrefs={setPrefs}
      onChartContract={chartIt} viewExpiry={iso => setExpiry(iso)} onChartBasket={chartBasket} />
  );
  const analytics = (
    <AnalyticsPanel legs={pricedLegs} stats={stats} compare={compare} spot={spot} cv={cv} atmIv={atmIv} base={base} bars={bars} oi={oiData}
      prefs={prefs} setPrefs={setPrefs} whatIf={whatIf} setWhatIf={setWhatIf} c={c} />
  );

  return (
    <Box ref={rootRef} data-testid="options-workspace" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: c.panel, color: c.text, fontSize: 12 }}>
      {/* ── top bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, px: 1.2, py: 0.6, borderBottom: `1px solid ${c.border}`, flexWrap: 'wrap', rowGap: 0.6 }}>
        {seg([['BTC', '₿ BTC'], ['ETH', 'Ξ ETH']], base, v => onBaseChange(v as 'BTC' | 'ETH'))}
        <Box>
          <Box sx={{ fontSize: 9.5, color: c.faint, letterSpacing: 0.6, fontWeight: 700 }}>INDEX</Box>
          <Box sx={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums', display: 'flex', alignItems: 'center', gap: 0.6 }}>
            {spot ? `${fmtPx(spot)}` : '—'}
            <Tooltip title={error ? `Chain: ${error}` : stale ? 'Waiting for live chain' : `Live · polled ${Math.round((Date.now() - (lastAt || 0)) / 1000)}s ago`}>
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: error ? c.red : stale ? c.amber : c.green, display: 'inline-block' }} />
            </Tooltip>
          </Box>
        </Box>
        <Box>
          <Box sx={{ fontSize: 9.5, color: c.faint, letterSpacing: 0.6, fontWeight: 700 }}>ATM IV</Box>
          <Box sx={{ fontSize: 15, fontWeight: 800 }}>{atmIv ? `${(atmIv * 100).toFixed(1)}%` : '—'}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, overflowX: 'auto', flex: '1 1 300px' }}>
          <Box sx={{ fontSize: 9.5, color: c.faint, letterSpacing: 0.6, fontWeight: 700, mr: 0.4 }}>EXPIRY</Box>
          {(chain?.expiries || []).map(e => {
            const on = e.iso === selIso;
            const hasLeg = legs.some(l => l.expiry === e.iso);
            return (
              <Box key={e.iso} component="button" type="button" onClick={() => { setExpiry(e.iso); onExpiryPick?.(e.iso); }}
                title={fnoExpiry === e.iso ? 'The chart’s FNO lines and panes show this expiry' : 'Select this expiry (the chart’s FNO data follows it)'}
                sx={{ all: 'unset', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, px: 0.9, py: 0.35, borderRadius: 1,
                  border: `1px solid ${on ? c.amber : 'transparent'}`, bgcolor: on ? c.amberBg : 'transparent', color: on ? c.text : c.dim,
                  position: 'relative', '&:hover': { bgcolor: on ? c.amberBg : c.hover } }}>
                {expLabel(e.iso)} <Box component="span" sx={{ color: on ? c.amber : c.faint, fontWeight: 600, fontSize: 10.5 }}>{timeLeft(e.settleMs)}</Box>
                {isMonthlyExpiry(e.iso) && (
                  <Tooltip title="Monthly expiry (last Friday of the month). Strangle playbook: open in the first ~5 days of the month.">
                    <Box component="span" sx={{ ml: 0.4, px: 0.35, borderRadius: 0.5, fontSize: 9, fontWeight: 800, color: c.blue, border: `1px solid ${c.blue}` }}>M</Box>
                  </Tooltip>
                )}
                {hasLeg && <Box component="span" sx={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', bgcolor: c.amber }} />}
                {fnoExpiry === e.iso && <Box component="span" data-testid="ws-fno-expiry" sx={{ position: 'absolute', left: 6, right: 6, bottom: 0, height: 2, borderRadius: 1, bgcolor: c.blue }} />}
              </Box>
            );
          })}
        </Box>
        {ctx3 && (
          <Tooltip title={`Last 3 completed daily candles: ${ctx3.ups} up / ${ctx3.downs} down, ${ctx3.chg >= 0 ? '+' : ''}${(ctx3.chg * 100).toFixed(1)}%. Flagged as a move when at least 2 of 3 closed the same way and the 3-day change is at least 5%. Playbook (weeklies only): sell after a 2-3 day move, stay away after a long quiet spell. Monthlies ignore this.`}>
            <Box sx={{ fontSize: 11, fontWeight: 700, px: 0.8, py: 0.25, borderRadius: 1, whiteSpace: 'nowrap',
              border: `1px solid ${ctx3.moved ? c.green : c.border}`, color: ctx3.moved ? c.green : c.dim }}>
              3d {ctx3.chg >= 0 ? '+' : ''}{(ctx3.chg * 100).toFixed(1)}% · {ctx3.moved ? 'moved — weekly window' : 'quiet'}
            </Box>
          </Tooltip>
        )}
        {chartContract && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, fontSize: 11.5, fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 1, border: `1px solid ${c.amber}`, color: c.amber, whiteSpace: 'nowrap' }}>
            <ShowChartIcon sx={{ fontSize: 14 }} /> {chartContract}
            <Tooltip title={`Back to the ${base} index chart`}>
              <Box component="button" type="button" data-testid="ws-contract-close" onClick={() => onChartContract(null)} sx={{ all: 'unset', cursor: 'pointer', ml: 0.4, color: c.dim, '&:hover': { color: c.text } }}>✕</Box>
            </Tooltip>
          </Box>
        )}
        <Tooltip title={prefs.chartOpen ? 'Hide the chart strip' : 'Show the underlying chart with leg strikes, breakevens and the profit zone'}>
          <Box component="button" type="button" onClick={() => setPrefs({ chartOpen: !prefs.chartOpen })}
            sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, fontSize: 12, fontWeight: 600, px: 0.9, py: 0.3, borderRadius: 1,
              border: `1px solid ${prefs.chartOpen ? c.blue : c.border}`, color: prefs.chartOpen ? c.blue : c.dim }}>
            <ShowChartIcon sx={{ fontSize: 15 }} /> Chart
          </Box>
        </Tooltip>
        {seg([['classic', 'Classic'], ['workspace', 'Workspace']], prefs.view, v => setPrefs({ view: v as any }))}
      </Box>

      {/* ── chart strip ── (fills the height while the panels are folded away) */}
      {(prefs.chartOpen || prefs.lowerCollapsed) && (
        <Box data-testid="ws-strip" sx={{ ...(prefs.lowerCollapsed ? { flex: 1, minHeight: 0 } : { height: prefs.chartHeight, flexShrink: 0 }), position: 'relative' }}>
          {renderChart(overlay)}
          {basketView && basketLegs.length > 0 && <BasketChart legs={basketLegs} cv={cv} c={c} onClose={() => setBasketView(false)} />}
        </Box>
      )}

      {/* ── divider: fold the panels away / size presets / drag to resize ── */}
      <Box data-testid="ws-divider"
        onMouseDown={e => {
          if (prefs.lowerCollapsed || !prefs.chartOpen || (e.target as HTMLElement).closest('button')) return;
          dragRef.current = { y: e.clientY, h: prefs.chartHeight }; e.preventDefault();
        }}
        onDoubleClick={e => { if (!(e.target as HTMLElement).closest('button')) setPrefs({ lowerCollapsed: !prefs.lowerCollapsed }); }}
        sx={{ height: 26, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.8, px: 1, borderTop: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}`,
          bgcolor: c.isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)', userSelect: 'none',
          cursor: prefs.lowerCollapsed || !prefs.chartOpen ? 'default' : 'row-resize' }}>
        <Tooltip title={prefs.lowerCollapsed ? 'Show the option chain, positions and payoff panels (double-click the bar too)' : 'Fold the chain, positions and payoff panels away — the chart takes the whole height (double-click the bar too)'}>
          <Box component="button" type="button" data-testid="ws-lower-toggle" onClick={() => setPrefs({ lowerCollapsed: !prefs.lowerCollapsed })}
            sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.3, fontSize: 11.5, fontWeight: 700, px: 0.7, py: 0.1, borderRadius: 0.8,
              border: `1px solid ${prefs.lowerCollapsed ? c.amber : c.border}`, color: prefs.lowerCollapsed ? c.amber : c.text, '&:hover': { bgcolor: c.hover } }}>
            {prefs.lowerCollapsed ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            {prefs.lowerCollapsed ? 'Show panels' : 'Hide panels'}
          </Box>
        </Tooltip>
        <Box sx={{ fontSize: 10.5, color: c.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {prefs.lowerCollapsed
            ? (pricedLegs.some(l => l.enabled)
              ? <>Chain · Positions · Payoff — <b style={{ color: c.text }}>{pricedLegs.filter(l => l.enabled && !l.closing).length} leg{pricedLegs.filter(l => l.enabled && !l.closing).length === 1 ? '' : 's'}</b>
                {stats.horizon != null && <> · max profit <b style={{ color: c.green }}>{fmtMoney(stats.maxProfit, prefs.ccy)}</b> · max loss <b style={{ color: c.red }}>{fmtMoney(stats.maxLoss, prefs.ccy)}</b>{stats.breakevens.length ? <> · BE {stats.breakevens.map(b => fmtStrike(Math.round(b))).join(' / ')}</> : null}</>}</>
              : 'Chain · Positions · Payoff')
            : 'Chain · Positions · Payoff'}
        </Box>
        <Box sx={{ flex: 1 }} />
        {!prefs.lowerCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ fontSize: 10, color: c.faint, mr: 0.2 }}>Chart</Box>
            {([[0.3, 'S', 'Small chart (about a third of the height)'], [0.5, 'M', 'Half and half'], [0.7, 'L', 'Large chart (about two thirds)']] as Array<[number, string, string]>).map(([f, lab, tip]) => (
              <Tooltip key={lab} title={tip} disableInteractive>
                <Box component="button" type="button" data-testid={`ws-size-${lab}`} onClick={() => setStrip(f)}
                  sx={{ all: 'unset', cursor: 'pointer', fontSize: 10.5, fontWeight: 700, width: 20, textAlign: 'center', py: 0.05, borderRadius: 0.6, border: `1px solid ${c.border}`, color: c.dim,
                    '&:hover': { bgcolor: c.hover, color: c.text } }}>{lab}</Box>
              </Tooltip>
            ))}
            <Box sx={{ fontSize: 10, color: c.faint, ml: 0.6, display: { xs: 'none', md: 'block' } }}>or drag this bar</Box>
          </Box>
        )}
      </Box>

      {/* ── body ── (kept mounted while folded so order forms keep their state) */}
      {prefs.view === 'workspace' ? (
        <Box data-testid="ws-lower" sx={{ flex: 1, minHeight: 0, display: prefs.lowerCollapsed ? 'none' : 'flex' }}>
          {prefs.chainCollapsed ? (
            <Box sx={{ width: 30, borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
              <Tooltip title="Show chain"><IconButton size="small" onClick={() => setPrefs({ chainCollapsed: false })}><KeyboardDoubleArrowRightIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Box sx={{ writingMode: 'vertical-rl', fontSize: 11, color: c.dim, mt: 1, letterSpacing: 1 }}>OPTION CHAIN</Box>
            </Box>
          ) : (
            <Box sx={{ width: '34%', minWidth: 330, maxWidth: 560, borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {chainControls}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ChainTable chain={chain} legs={legs} side={prefs.chainSide} cols={prefs.chainCols} range={prefs.range} c={c} onAdd={onAdd} onHover={onHover} recentre={recentre} onChart={chartIt} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', px: 1, py: 0.4, borderTop: `1px solid ${c.border}`, fontSize: 10.5, color: c.dim }}>
                <span><Box component="span" sx={{ color: c.red }}>■</Box> Ask to buy</span>
                <span><Box component="span" sx={{ color: c.green }}>■</Box> Bid to sell</span>
                <span><Box component="span" sx={{ color: c.blue, opacity: 0.6 }}>■</Box> ITM</span>
                <Box sx={{ flex: 1 }} />
                <span>{chain?.chain?.rows.length ?? 0} strikes</span>
              </Box>
            </Box>
          )}
          <Box sx={{ flex: '1 1 30%', minWidth: 300, borderRight: `1px solid ${c.border}`, minHeight: 0 }}>{legsPanel()}</Box>
          {prefs.analyticsCollapsed ? (
            <Box sx={{ width: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
              <Tooltip title="Show analytics"><IconButton size="small" onClick={() => setPrefs({ analyticsCollapsed: false })}><KeyboardDoubleArrowLeftIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              <Box sx={{ writingMode: 'vertical-rl', fontSize: 11, color: c.dim, mt: 1, letterSpacing: 1 }}>PAYOFF</Box>
            </Box>
          ) : (
            <Box sx={{ flex: '1 1 38%', minWidth: 360, minHeight: 0, position: 'relative' }}>
              {analytics}
              <Tooltip title="Collapse analytics">
                <IconButton size="small" onClick={() => setPrefs({ analyticsCollapsed: true })} sx={{ position: 'absolute', top: 6, right: 52, p: 0.3 }}>
                  <KeyboardDoubleArrowRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ) : (
        <Box data-testid="ws-lower" sx={{ flex: 1, minHeight: 0, display: prefs.lowerCollapsed ? 'none' : 'flex', flexDirection: 'column' }}>
          <Box sx={{ flexShrink: 0, borderBottom: `1px solid ${c.border}` }}>
            {chainControls}
            <Box sx={{ height: 236 }}>
              <ChainStrip chain={chain} legs={legs} range={prefs.range} c={c} onAdd={onAdd} recentre={recentre} onChart={chartIt} />
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Box sx={{ flex: '0 0 40%', minWidth: 320, borderRight: `1px solid ${c.border}`, minHeight: 0 }}>{legsPanel(true)}</Box>
            <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>{analytics}</Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OptionsWorkspace;
